import { Box, Flex } from "@chakra-ui/react";
import { InputBaseWrapper } from "./InputBaseWrapper";
import { Col } from "antd";
import Field from "components/Input/Field";
import SelectFormaPagamento from "components/Input/SelectFormaPagamento";
import SelectContaBancaria from "components/Input/SelectContaBancaria";
import { OperacaoEnum } from "resources/enums/conta";
import { Control, UseFormSetValue } from "react-hook-form";
import SelectContaCategoria from "ui/components/Input/SelectContaCategoria";
import { PagarContaForm } from "core/features/conta/typings";
import CardValorLiquidar from "./CardValorLiquidar";

interface FormLiquidarContaProps {
  operacao: OperacaoEnum;
  control: Control<any, any>;
  setValue: UseFormSetValue<any>;
  isLoadingEncargos: boolean;
  isFetchingEncargos: boolean;
  form?: PagarContaForm;
  fieldsRoot?: string;
  integradoPagamentoOnline?: boolean;
  required?: boolean;
  fixedDataMovimentacao?: string;
}

export default function FormLiquidarConta({
  control,
  operacao,
  isFetchingEncargos,
  isLoadingEncargos,
  form,
  fieldsRoot,
  required = true,
  fixedDataMovimentacao,
  setValue,
}: FormLiquidarContaProps) {
  const isReceita = operacao === OperacaoEnum.Receita;
  const encargosColSize = 6;

  const isTaxaAdicionalNegativa =
    (form?.taxaAdicional || 0) * (form?.taxaAdicionalCategoriaOperacao || 0) <
    0;

  const isTaxaAdicionalPositiva =
    (form?.taxaAdicional || 0) * (form?.taxaAdicionalCategoriaOperacao || 0) >
    0;

  const totalEncargos = isTaxaAdicionalNegativa ? form?.taxaAdicional! : 0;

  const totalAcrescimos =
    (form?.juros || 0) +
    (form?.multa || 0) +
    (isTaxaAdicionalPositiva ? form?.taxaAdicional ?? 0 : 0);

  return (
    <Box>
      <InputBaseWrapper gutter={16}>
        <Box as={Col} xs={12}>
          <Field
            control={control}
            name={
              fieldsRoot ? `${fieldsRoot}.${"dataPagamento"}` : "dataPagamento"
            }
            label="Data do pagamento"
            rules={{
              required,
              max: "2100-01-01",
            }}
            inputProps={{
              type: "date",
              max: "2100-01-01",
            }}
          />
        </Box>

        <Box as={Col} xs={12}>
          <Field
            control={control}
            name={
              fieldsRoot
                ? `${fieldsRoot}.${"formaPagamentoId"}`
                : "formaPagamentoId"
            }
            label="Forma de pagamento"
            rules={{
              required,
            }}
            render={({ field, isInvalid }) => (
              <SelectFormaPagamento
                isInvalid={isInvalid}
                onChange={field.onChange}
                value={field.value}
              />
            )}
          />
        </Box>
      </InputBaseWrapper>

      <InputBaseWrapper gutter={16}>
        <Box as={Col} xs={12}>
          <Field
            shouldUnregister
            control={control}
            name={
              fieldsRoot ? `${fieldsRoot}.${"tenantContaId"}` : "tenantContaId"
            }
            label="Conta bancária"
            rules={{
              required,
            }}
            render={({ field, isInvalid }) => (
              <SelectContaBancaria
                isInvalid={isInvalid}
                onChange={field.onChange}
                value={field.value}
                incluirInativas={false}
              />
            )}
          />
        </Box>

        {isReceita ? (
          <Box as={Col} xs={12}>
            <Field
              control={control}
              name={
                fieldsRoot
                  ? `${fieldsRoot}.${"dataMovimentacao"}`
                  : "dataMovimentacao"
              }
              label={`Movimentação de entrada no banco`}
              rules={{
                max: "2100-01-01",
              }}
              inputProps={{
                type: "date",
                disabled: !!fixedDataMovimentacao,
                max: "2100-01-01",
              }}
            />
          </Box>
        ) : null}
      </InputBaseWrapper>

      <InputBaseWrapper gutter={16} mb={6}>
        <Box as={Col} xs={encargosColSize}>
          <Field
            control={control}
            name={fieldsRoot ? `${fieldsRoot}.${"multa"}` : "multa"}
            label="Multa"
            inputProps={{
              type: "money",
              minValue: 0,
              disabled: isLoadingEncargos || isFetchingEncargos,
            }}
          />
        </Box>

        <Box as={Col} xs={encargosColSize}>
          <Field
            control={control}
            name={fieldsRoot ? `${fieldsRoot}.${"juros"}` : "juros"}
            label="Juros"
            inputProps={{
              type: "money",
              minValue: 0,
              disabled: isLoadingEncargos || isFetchingEncargos,
            }}
          />
        </Box>

        {isReceita ? (
          <>
            <Box as={Col} xs={encargosColSize}>
              <Field
                control={control}
                name={
                  fieldsRoot
                    ? `${fieldsRoot}.${"taxaAdicional"}`
                    : "taxaAdicional"
                }
                label="Taxa adicional"
                inputProps={{
                  type: "money",
                  minValue: 0,
                }}
              />
            </Box>

            <Box as={Col} xs={encargosColSize}>
              <Field
                control={control}
                name={
                  fieldsRoot
                    ? `${fieldsRoot}.${"taxaRegistro"}`
                    : "taxaRegistro"
                }
                label="Taxa de registro"
                inputProps={{
                  type: "money",
                  minValue: 0,
                }}
              />
            </Box>
          </>
        ) : null}
      </InputBaseWrapper>

      {isReceita ? (
        <InputBaseWrapper gutter={16} mb={6}>
          <Box as={Col} xs={12}>
            <Field
              control={control}
              name={
                fieldsRoot
                  ? `${fieldsRoot}.${"taxaAdicionalCategoriaId"}`
                  : "taxaAdicionalCategoriaId"
              }
              label="Categoria da Taxa Adicional"
              rules={{
                required:
                  required && !!form?.taxaAdicional && form?.taxaAdicional > 0,
              }}
              render={({ field, isInvalid }) => (
                <SelectContaCategoria
                  disableNotClassifiableNodes
                  isInvalid={isInvalid}
                  value={field.value}
                  onChange={(v, item) => {
                    field.onChange(v);
                    setValue(
                      fieldsRoot
                        ? `${fieldsRoot}.${"taxaAdicionalCategoriaOperacao"}`
                        : "taxaAdicionalCategoriaOperacao",
                      item?.operacao ?? null
                    );
                  }}
                />
              )}
            />
          </Box>
        </InputBaseWrapper>
      ) : null}

      <Flex gap={3} mb={6}>
        <Box flex={1}>
          <CardValorLiquidar title="Valor original" value={form?.valor} />
        </Box>

        <Box flex={1}>
          <CardValorLiquidar title="Acréscimos" value={totalAcrescimos} />
        </Box>

        {isReceita ? (
          <Box flex={1}>
            <CardValorLiquidar title="Encargos" value={totalEncargos} />
          </Box>
        ) : null}

        <Box flex={1}>
          <CardValorLiquidar title="Valor pago" value={form?.valorPago} />
        </Box>

        <Box flex={1}>
          <CardValorLiquidar
            title="Valor líquido"
            value={(form?.valorPago ?? 0) - totalEncargos}
          />
        </Box>
      </Flex>
    </Box>
  );
}
